import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3, Compass, Server, Trash2, ArrowRight, Loader2,
  GitCompareArrows, AlertCircle, Plus, RefreshCw, TrendingUp,
  Shield, ChevronDown, ChevronUp
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const PERSONA_META = {
  tax: { title: 'Tax Director', icon: Compass, color: '#0066CC', subtitle: 'Compliance & Regulatory' },
  it: { title: 'IT Director', icon: Server, color: '#14b8a6', subtitle: 'Architecture & Integration' },
  finance: { title: 'CFO / Finance', icon: BarChart3, color: '#8b5cf6', subtitle: 'Cost, Accuracy & ROI' },
};

const DIMENSION_LABELS = {
  filingRisk: 'Filing Risk',
  costPressure: 'Cost Pressure',
  visibility: 'Visibility',
  fragmentation: 'Vendor Landscape',
  readiness: 'Future Readiness',
  integrationComplexity: 'Integration Complexity',
  dataSecurity: 'Data Security',
  scalability: 'Scalability',
  architecture: 'ERP Connection',
  techDebt: 'Technical Debt',
  tco: 'Total Cost of Ownership',
  accuracy: 'Financial Accuracy',
  reconEffort: 'Reconciliation Effort',
  auditReadiness: 'Audit Readiness',
  roiVisibility: 'ROI Visibility',
};

const SCORE_LABELS = [
  { min: 80, label: 'Strong', color: '#10b981' },
  { min: 60, label: 'Fair', color: '#84cc16' },
  { min: 40, label: 'Exposed', color: '#f59e0b' },
  { min: 20, label: 'At Risk', color: '#f97316' },
  { min: 0, label: 'Critical', color: '#ef4444' },
];
const getScoreLabel = (s) => SCORE_LABELS.find(l => s >= l.min) || SCORE_LABELS[4];

const formatCurrency = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}K`;
  return `${Math.round(n)}`;
};

/* ─── Result Card (default view) ─── */
const ResultCard = ({ personaKey, meta, result, onDelete, index }) => {
  const Icon = meta.icon;
  const scoreInfo = result ? getScoreLabel(result.health_score) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className={`rounded-2xl border overflow-hidden ${result ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-50 border-dashed border-slate-300'}`}
      data-testid={`result-card-${personaKey}`}
    >
      <div className="h-1" style={{ backgroundColor: meta.color, opacity: result ? 1 : 0.2 }} />
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${meta.color}12` }}>
            <Icon className="w-5 h-5" style={{ color: meta.color }} strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-heading text-base font-bold text-taxera-charcoal">{meta.title}</h3>
            <p className="text-xs text-slate-400">{meta.subtitle}</p>
          </div>
        </div>

        {result ? (
          <>
            <div className="mb-4">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-heading text-4xl font-bold" style={{ color: scoreInfo.color }}>{result.health_score}</span>
                <span className="text-sm text-slate-400">/ 100</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: scoreInfo.color }} />
                <span className="text-xs font-semibold" style={{ color: scoreInfo.color }}>{scoreInfo.label}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-2xs text-slate-400 uppercase font-bold">Countries</div>
                <div className="font-heading text-lg font-bold text-taxera-charcoal">{result.country_count}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-2xs text-slate-400 uppercase font-bold">Annual cost</div>
                <div className="font-heading text-lg font-bold text-red-500">{result.cost_data?.total ? `\u20AC${formatCurrency(result.cost_data.total)}` : '\u2014'}</div>
              </div>
            </div>
            {result.cost_data?.savings && (
              <div className="bg-emerald-50 rounded-lg p-3 mb-4">
                <div className="text-2xs text-emerald-600 uppercase font-bold">Projected savings</div>
                <div className="font-heading text-xl font-bold text-emerald-600">{`\u20AC${formatCurrency(result.cost_data.savings)}`}/yr</div>
              </div>
            )}
            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
              <Link to="/evaluate" className="text-xs font-semibold text-taxera-blue hover:underline flex items-center gap-1" data-testid={`retake-${personaKey}`}>
                <RefreshCw className="w-3 h-3" /> Retake
              </Link>
              <button onClick={() => onDelete(personaKey)} className="text-xs text-slate-400 hover:text-red-500 flex items-center gap-1" data-testid={`delete-${personaKey}`}>
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-slate-400 mb-4">Not completed yet</p>
            <Link to="/evaluate" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-full text-sm font-semibold text-taxera-charcoal hover:border-slate-300 transition-all" data-testid={`start-${personaKey}`}>
              <Plus className="w-3.5 h-3.5" /> Take Assessment
            </Link>
          </div>
        )}
      </div>
    </motion.div>
  );
};

/* ─── Comparison: Score Overview Row ─── */
const ComparisonOverview = ({ results }) => {
  const avgScore = Math.round(results.reduce((s, r) => s + r.health_score, 0) / results.length);
  const avgInfo = getScoreLabel(avgScore);
  const totalCost = results.reduce((s, r) => s + (r.cost_data?.total || 0), 0);
  const totalSavings = results.reduce((s, r) => s + (r.cost_data?.savings || 0), 0);

  return (
    <div className="grid grid-cols-3 gap-4 mb-8" data-testid="comparison-overview">
      <div className="bg-white/5 rounded-2xl p-5 border border-white/10 text-center">
        <div className="text-2xs font-bold tracking-[0.15em] text-slate-500 uppercase mb-2">Avg Health Score</div>
        <div className="font-heading text-5xl font-bold" style={{ color: avgInfo.color }}>{avgScore}</div>
        <div className="text-xs font-semibold mt-1" style={{ color: avgInfo.color }}>{avgInfo.label}</div>
      </div>
      <div className="bg-white/5 rounded-2xl p-5 border border-white/10 text-center">
        <div className="text-2xs font-bold tracking-[0.15em] text-slate-500 uppercase mb-2">Combined Annual Cost</div>
        <div className="font-heading text-4xl font-bold text-red-400">{`\u20AC${formatCurrency(totalCost)}`}</div>
        <div className="text-xs text-slate-500 mt-1">across {results.length} perspectives</div>
      </div>
      <div className="bg-white/5 rounded-2xl p-5 border border-white/10 text-center">
        <div className="text-2xs font-bold tracking-[0.15em] text-slate-500 uppercase mb-2">Total Savings Potential</div>
        <div className="font-heading text-4xl font-bold text-emerald-400">{`\u20AC${formatCurrency(totalSavings)}`}</div>
        <div className="text-xs text-emerald-400/70 mt-1">/year with Taxera</div>
      </div>
    </div>
  );
};

/* ─── Comparison: Persona Score Column ─── */
const PersonaScoreColumn = ({ result }) => {
  const meta = PERSONA_META[result.persona];
  const scoreInfo = getScoreLabel(result.health_score);
  const Icon = meta?.icon;
  const scores = result.pain_scores || {};

  return (
    <div className="bg-white/5 rounded-2xl p-5 border border-white/10" data-testid={`compare-card-${result.persona}`}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4" style={{ color: meta.color }} strokeWidth={1.5} />}
        <span className="text-sm font-bold text-white">{meta?.title}</span>
      </div>
      <div className="flex items-baseline gap-2 mb-1">
        <span className="font-heading text-5xl font-bold" style={{ color: scoreInfo.color }}>{result.health_score}</span>
        <span className="text-sm text-slate-600">/ 100</span>
      </div>
      <div className="text-xs font-semibold mb-5" style={{ color: scoreInfo.color }}>{scoreInfo.label}</div>

      <div className="space-y-2.5">
        {Object.entries(scores).map(([key, score]) => {
          const barColor = getScoreLabel(score * 5).color;
          return (
            <div key={key} className="group">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-2xs text-slate-500 truncate">{DIMENSION_LABELS[key] || key}</span>
                <span className="text-2xs font-bold" style={{ color: barColor }}>{score}/20</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ backgroundColor: barColor, width: `${(score / 20) * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Comparison: Cost Table ─── */
const CostComparisonTable = ({ results }) => {
  const totalCurrent = results.reduce((s, r) => s + (r.cost_data?.total || 0), 0);
  const totalTaxera = results.reduce((s, r) => s + (r.cost_data?.taxeraTotal || 0), 0);
  const totalSavings = results.reduce((s, r) => s + (r.cost_data?.savings || 0), 0);

  return (
    <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden" data-testid="cost-comparison-table">
      <div className="px-5 py-3 border-b border-white/10">
        <h3 className="text-sm font-bold text-taxera-teal uppercase tracking-wide">Cost Impact Comparison</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left text-2xs font-bold text-slate-500 uppercase tracking-wide px-5 py-3">Persona</th>
              <th className="text-right text-2xs font-bold text-slate-500 uppercase tracking-wide px-5 py-3">Current Cost</th>
              <th className="text-right text-2xs font-bold text-slate-500 uppercase tracking-wide px-5 py-3">With Taxera</th>
              <th className="text-right text-2xs font-bold text-slate-500 uppercase tracking-wide px-5 py-3">Annual Savings</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r) => {
              const meta = PERSONA_META[r.persona];
              const Icon = meta?.icon;
              const savings = (r.cost_data?.savings || 0);
              return (
                <tr key={r.persona} className="border-b border-white/5" data-testid={`cost-row-${r.persona}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      {Icon && <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} strokeWidth={1.5} />}
                      <span className="text-xs font-semibold text-white">{meta?.title}</span>
                    </div>
                  </td>
                  <td className="text-right px-5 py-3 font-heading font-bold text-red-400">{r.cost_data?.total ? `\u20AC${formatCurrency(r.cost_data.total)}` : '\u2014'}</td>
                  <td className="text-right px-5 py-3 font-heading font-bold text-white">{r.cost_data?.taxeraTotal ? `\u20AC${formatCurrency(r.cost_data.taxeraTotal)}` : '\u2014'}</td>
                  <td className="text-right px-5 py-3 font-heading font-bold text-emerald-400">{savings > 0 ? `\u20AC${formatCurrency(savings)}` : '\u2014'}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-white/10 bg-white/5">
              <td className="px-5 py-3 text-xs font-bold text-taxera-teal uppercase">Combined Total</td>
              <td className="text-right px-5 py-3 font-heading text-base font-bold text-red-400">{`\u20AC${formatCurrency(totalCurrent)}`}</td>
              <td className="text-right px-5 py-3 font-heading text-base font-bold text-white">{`\u20AC${formatCurrency(totalTaxera)}`}</td>
              <td className="text-right px-5 py-3 font-heading text-base font-bold text-emerald-400">{`\u20AC${formatCurrency(totalSavings)}`}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

/* ─── Comparison: Key Insights ─── */
const KeyInsights = ({ results }) => {
  let weakest = { dim: '', score: 21, persona: '' };
  let strongest = { dim: '', score: -1, persona: '' };

  results.forEach(r => {
    const meta = PERSONA_META[r.persona];
    Object.entries(r.pain_scores || {}).forEach(([dim, score]) => {
      if (score < weakest.score) weakest = { dim, score, persona: meta?.title || r.persona };
      if (score > strongest.score) strongest = { dim, score, persona: meta?.title || r.persona };
    });
  });

  const totalSavings = results.reduce((s, r) => s + (r.cost_data?.savings || 0), 0);
  const lowestPersona = results.reduce((a, b) => (a.health_score < b.health_score ? a : b));
  const lowestMeta = PERSONA_META[lowestPersona.persona];

  return (
    <div className="bg-white/5 rounded-2xl border border-white/10 p-5 mt-6" data-testid="key-insights">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-4 h-4 text-taxera-teal" strokeWidth={1.5} />
        <h3 className="text-sm font-bold text-taxera-teal uppercase tracking-wide">Key Insights</h3>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-red-500/5 rounded-xl p-4 border border-red-500/10">
          <div className="text-2xs font-bold text-red-400 uppercase tracking-wide mb-1">Biggest Risk</div>
          <div className="text-sm text-white font-semibold">{DIMENSION_LABELS[weakest.dim] || weakest.dim}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {weakest.score}/20 via {weakest.persona}
          </div>
        </div>
        <div className="bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/10">
          <div className="text-2xs font-bold text-emerald-400 uppercase tracking-wide mb-1">Strongest Area</div>
          <div className="text-sm text-white font-semibold">{DIMENSION_LABELS[strongest.dim] || strongest.dim}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {strongest.score}/20 via {strongest.persona}
          </div>
        </div>
        <div className="bg-amber-500/5 rounded-xl p-4 border border-amber-500/10">
          <div className="text-2xs font-bold text-amber-400 uppercase tracking-wide mb-1">Priority Focus</div>
          <div className="text-sm text-white font-semibold">{lowestMeta?.title} perspective</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Lowest score ({lowestPersona.health_score}/100) — start here
          </div>
        </div>
      </div>
      {totalSavings > 0 && (
        <p className="text-xs text-slate-500 mt-4 pt-3 border-t border-white/10">
          Across all assessed perspectives, the combined savings potential is{' '}
          <span className="font-bold text-emerald-400">{`\u20AC${formatCurrency(totalSavings)}`}/year</span>. Addressing the{' '}
          <span className="text-white font-semibold">{lowestMeta?.title}</span> pain points first
          will deliver the highest ROI.
        </p>
      )}
    </div>
  );
};

/* ─── Full Comparison View ─── */
const ComparisonView = ({ results, missingPersonas }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    className="mb-8"
    data-testid="comparison-view"
  >
    <div className="bg-slate-900 rounded-3xl p-8 md:p-10 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(20,163,184,0.12)_0%,transparent_60%)]" />
      <div className="relative z-10">
        <h2 className="font-heading text-2xl font-bold text-white mb-2">Persona Comparison</h2>
        <p className="text-sm text-slate-400 mb-8">How compliance looks through each lens — same organization, different pain points.</p>

        <ComparisonOverview results={results} />

        {/* Score columns */}
        <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: `repeat(${results.length}, 1fr)` }}>
          {results.map((r) => <PersonaScoreColumn key={r.persona} result={r} />)}
        </div>

        <CostComparisonTable results={results} />
        <KeyInsights results={results} />

        {/* Missing personas prompt */}
        {missingPersonas.length > 0 && (
          <div className="mt-6 flex items-center gap-3 bg-white/5 rounded-xl p-4 border border-white/10">
            <Plus className="w-5 h-5 text-taxera-teal shrink-0" />
            <p className="text-sm text-slate-400">
              Complete the <span className="text-white font-semibold">{missingPersonas.map(p => PERSONA_META[p]?.title).join(' & ')}</span> assessment{missingPersonas.length > 1 ? 's' : ''} to see the full picture.
            </p>
            <Link to="/evaluate" className="shrink-0 text-xs font-semibold text-taxera-teal hover:underline">Take it now</Link>
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

/* ─── Main Page ─── */
const DashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
      return;
    }
    if (user) {
      (async () => {
        try {
          const res = await fetch(`${API}/api/diagnostics/mine`, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            setResults(data.results || []);
          }
        } catch (e) { console.error('Failed to fetch diagnostics:', e); }
        finally { setLoading(false); }
      })();
    }
  }, [user, authLoading, navigate]);

  const deleteResult = async (persona) => {
    await fetch(`${API}/api/diagnostics/${persona}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    setResults(r => r.filter(d => d.persona !== persona));
  };

  if (authLoading || loading) {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-taxera-blue" />
        </div>
      </PageLayout>
    );
  }

  const completedPersonas = results.map(r => r.persona);
  const missingPersonas = Object.keys(PERSONA_META).filter(p => !completedPersonas.includes(p));

  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-2" data-testid="dashboard-title">
                Your Diagnostics
              </h1>
              <p className="text-base text-slate-500">
                {results.length === 0
                  ? 'No saved diagnostics yet. Complete an assessment to see your results here.'
                  : `${results.length} of 3 persona assessments completed.`}
              </p>
            </div>
            {results.length >= 2 && (
              <button
                onClick={() => setShowComparison(!showComparison)}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all ${
                  showComparison ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-taxera-charcoal hover:border-slate-300'
                }`}
                data-testid="toggle-comparison-btn"
              >
                <GitCompareArrows className="w-4 h-4" />
                {showComparison ? 'Hide Comparison' : 'Compare Personas'}
              </button>
            )}
          </div>
        </motion.div>

        {/* Empty state */}
        {results.length === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16 bg-slate-50 rounded-3xl border border-slate-200" data-testid="empty-dashboard">
            <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">No diagnostics yet</h3>
            <p className="text-sm text-slate-500 mb-6">Complete a diagnostic assessment to save your results and unlock persona comparison.</p>
            <Link to="/evaluate" className="group inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-lg transition-all" data-testid="start-first-diagnostic">
              Start Your First Diagnostic <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        )}

        {/* Result cards */}
        {results.length > 0 && !showComparison && (
          <div className="grid md:grid-cols-3 gap-5 mb-8">
            {Object.entries(PERSONA_META).map(([key, meta], i) => {
              const result = results.find(r => r.persona === key);
              return <ResultCard key={key} personaKey={key} meta={meta} result={result} onDelete={deleteResult} index={i} />;
            })}
          </div>
        )}

        {/* Comparison View */}
        <AnimatePresence>
          {showComparison && results.length >= 2 && (
            <ComparisonView results={results} missingPersonas={missingPersonas} />
          )}
        </AnimatePresence>

        {/* CTA to complete missing */}
        {results.length > 0 && results.length < 3 && !showComparison && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-slate-50 rounded-2xl border border-slate-200 p-6 flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-1">Get the full picture</h3>
              <p className="text-sm text-slate-500">Complete all 3 persona assessments to unlock the full comparison view.</p>
            </div>
            <Link to="/evaluate" className="inline-flex items-center gap-2 px-6 py-3 taxera-gradient text-white rounded-full font-semibold text-sm hover:shadow-lg transition-all" data-testid="complete-more-btn">
              Continue Assessments <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        )}
      </div>
    </PageLayout>
  );
};

export default DashboardPage;
